/* ============================================================
   cart-drawer.js — Web Component Shopify OS 2.0
   Gestisce apertura/chiusura e aggiornamento via Section Rendering API.
   ============================================================ */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this._overlay = document.getElementById('overlay');
    this._setupListeners();
    // Espone API globale
    window.upsilOpenCart  = () => this.open();
    window.upsilAddVariant = (id, qty, opts) => this.addItem(id, qty, opts);
    window.upsilAddMany   = (ids) => this.addMany(ids);
  }

  /* ---------- open / close ---------- */
  open() {
    const aside = this.querySelector('#cartDrawer');
    aside && aside.classList.add('is-open');
    this._overlay && this._overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  close() {
    const aside = this.querySelector('#cartDrawer');
    aside && aside.classList.remove('is-open');
    this._overlay && this._overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  /* ---------- Section Rendering API ---------- */
  async refresh() {
    try {
      const res = await fetch('/?section_id=cart-drawer', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!res.ok) return;
      const html = await res.text();
      const doc  = new DOMParser().parseFromString(html, 'text/html');

      const newBody = doc.getElementById('cartBody');
      const newFoot = doc.getElementById('cartFoot');
      const thisBody = this.querySelector('#cartBody');
      const thisFoot = this.querySelector('#cartFoot');
      /* il 3x2 era gia' attivo prima di questo aggiornamento? serve a
         festeggiare solo nel momento in cui scatta, non a ogni modifica */
      const wasActive = !!thisFoot?.querySelector('.cart-progress.is-active');
      const oldPct    = thisFoot?.querySelector('.cart-progress i')?.style.width;

      if (newBody && thisBody) thisBody.innerHTML = newBody.innerHTML;
      if (newFoot && thisFoot) {
        thisFoot.innerHTML  = newFoot.innerHTML;
        thisFoot.style.display = newFoot.style.display || '';
        /* la barra riparte dalla larghezza di prima, cosi' si vede crescere */
        const bar = thisFoot.querySelector('.cart-progress i');
        if (bar && oldPct && oldPct !== bar.style.width) {
          const target = bar.style.width;
          bar.style.width = oldPct;
          requestAnimationFrame(() => requestAnimationFrame(() => { bar.style.width = target; }));
        }
        if (!wasActive && thisFoot.querySelector('.cart-progress.is-active')) this._celebrate(thisFoot);
      }

      // aggiorna badge contatore
      await this._syncCount();
    } catch (err) {
      console.error('[CartDrawer] refresh error', err);
    }
  }

  /* Festa del 3x2: coriandoli dorati che partono dalla barra dell'offerta.
     Solo CSS e una ventina di elementi che si tolgono da soli. Niente se la
     persona ha chiesto al sistema di ridurre le animazioni. */
  _celebrate(foot) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const bar = foot.querySelector('.cart-progress');
    const msg = foot.querySelector('.cart-offer__msg');
    if (!bar) return;
    msg?.classList.add('is-pop');
    const box = document.createElement('div');
    box.className = 'cart-confetti';
    box.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < 22; i++) {
      const c = document.createElement('i');
      c.style.setProperty('--x', (Math.random() * 100).toFixed(1) + '%');
      c.style.setProperty('--dx', ((Math.random() - .5) * 90).toFixed(0) + 'px');
      c.style.setProperty('--dy', (-(90 + Math.random() * 130)).toFixed(0) + 'px');
      c.style.setProperty('--r', (Math.random() * 540 - 270).toFixed(0) + 'deg');
      c.style.setProperty('--d', (Math.random() * .18).toFixed(2) + 's');
      c.className = 'k' + (i % 3);
      box.appendChild(c);
    }
    bar.parentNode.insertBefore(box, bar);
    setTimeout(() => { box.remove(); msg?.classList.remove('is-pop'); }, 1700);
  }

  async _syncCount() {
    try {
      const res  = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await res.json();
      document.querySelectorAll('.cart-count').forEach(el => {
        const changed = String(el.dataset.count) !== String(cart.item_count);
        el.textContent  = cart.item_count;
        el.dataset.count = cart.item_count;
        /* scatto del contatore quando il numero cambia */
        if (changed) {
          el.classList.remove('is-bump');
          void el.offsetWidth;
          el.classList.add('is-bump');
        }
      });
    } catch (_) {}
  }

  /* ---------- cart API ---------- */
  async addItem(variantId, qty = 1, opts = {}) {
    const routes = window.UPSIL?.routes || {};
    try {
      const res  = await fetch(routes.cart_add || '/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: qty }] })
      });
      const data = await res.json();
      if (data.status) {
        window.upsilToast?.(data.description || 'Non disponibile');
        return null;
      }
      await this.refresh();
      this.open();
      const label = (opts.title ? opts.title + ' — ' : '') +
        (window.UPSIL?.strings?.added || 'Aggiunto al carrello');
      window.upsilToast?.(label);
      return data;
    } catch (_) {
      window.upsilToast?.('Errore, riprova');
      return null;
    }
  }

  async changeLine(line, qty) {
    const routes = window.UPSIL?.routes || {};
    try {
      await fetch(routes.cart_change || '/cart/change.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ line, quantity: qty })
      });
      await this.refresh();
    } catch (_) {
      window.upsilToast?.('Errore, riprova');
    }
  }

  async addMany(ids) {
    const items  = (ids || []).map(id => ({ id, quantity: 1 }));
    if (!items.length) return;
    const routes = window.UPSIL?.routes || {};
    try {
      await fetch(routes.cart_add || '/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ items })
      });
      await this.refresh();
      this.open();
      window.upsilToast?.(items.length + ' capi aggiunti');
    } catch (_) {
      window.upsilToast?.('Errore, riprova');
    }
  }

  /* ---------- listeners ---------- */
  _setupListeners() {
    this.addEventListener('click', e => {
      if (e.target.closest('[data-close]')) { this.close(); return; }
      const lineBtn = e.target.closest('[data-line][data-to]');
      if (lineBtn) {
        e.preventDefault();
        this.changeLine(parseInt(lineBtn.dataset.line, 10), parseInt(lineBtn.dataset.to, 10));
      }
    });
  }
}

customElements.define('cart-drawer', CartDrawer);
